import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { Tab, Tabs } from '@mui/material'

import PageTitle from 'components/atoms/PageTitle'
import LogisticsContent from 'components/organisms/Logistics'
import ReleaseLoaderContent from 'components/organisms/ReleaseLoader'

import {
  ROLES_ARVAL,
  ROLE_ADMIN,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  ROLE_SUPER_ADMIN,
  ROLE_TEAM_RELEASE,
  useAuth,
} from '../../auth'

// REL-F04 · "Caricamento dati" hub. Groups the two Arval loaders under a single
// menu entry: the pre-existing Logistica (storage-place) loader and the new
// Release-flag loader (REL-11). Access mirrors the old Logistics page
// (ROLES_ARVAL) + teamRelease; write access is gated per-loader:
//  - Logistica  → read-only for readOnly / b2c / postSales
//  - Release    → writable by the BE loader gate (superAdmin/admin/logistics/teamRelease)
// teamRelease is a Release-only user: it reaches this page ONLY for the Release
// tab (Logistica hidden) and can use the loader (BE: requiresReleaseLoaderUser).
export default function DataLoad() {
  const { t } = useTranslation()
  const { hasOneOfRoles } = useAuth()

  const [tab, setTab] = useState(0)

  // teamRelease is NOT in ROLES_ARVAL, so it can reach this page but only for
  // the Release tab (Logistica stays hidden).
  const isTeamRelease = useMemo(
    () => hasOneOfRoles([ROLE_TEAM_RELEASE]),
    [hasOneOfRoles]
  )

  const canAccess = useMemo(
    () => hasOneOfRoles(ROLES_ARVAL) || isTeamRelease,
    [hasOneOfRoles, isTeamRelease]
  )

  const logisticsReadOnly = useMemo(
    () => hasOneOfRoles([ROLE_READ_ONLY, ROLE_B2C, ROLE_POST_SALES]),
    [hasOneOfRoles]
  )

  const releaseReadOnly = useMemo(
    () =>
      !hasOneOfRoles([
        ROLE_SUPER_ADMIN,
        ROLE_ADMIN,
        ROLE_LOGISTICS,
        ROLE_TEAM_RELEASE,
      ]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  // teamRelease: solo il loader Release (nessuna tab Logistica), scrivibile.
  if (isTeamRelease) {
    return (
      <div className="content-container">
        <PageTitle label={t('dataLoad.title')} />
        <Tabs value={0} sx={{ mb: 2 }}>
          <Tab label={t('dataLoad.tab.release')} data-testid="tab-release" />
        </Tabs>
        <ReleaseLoaderContent contentIsReadOnly={releaseReadOnly} />
      </div>
    )
  }

  return (
    <div className="content-container">
      <PageTitle label={t('dataLoad.title')} />
      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab label={t('dataLoad.tab.logistics')} data-testid="tab-logistics" />
        <Tab label={t('dataLoad.tab.release')} data-testid="tab-release" />
      </Tabs>
      {tab === 0 && <LogisticsContent contentIsReadOnly={logisticsReadOnly} />}
      {tab === 1 && (
        <ReleaseLoaderContent contentIsReadOnly={releaseReadOnly} />
      )}
    </div>
  )
}
